/**
 * Widget utilizzo piano abbonamento per utenti non-admin.
 * Mostra il consumo cumulativo di parole e query rispetto ai limiti dell'azienda.
 */

import React, { useState, useEffect } from 'react';
import { api } from '../services/api';

const PLAN_COLORS = {
  PRO:        'text-blue-600 bg-blue-50 border-blue-200',
  BUSINESS:   'text-purple-600 bg-purple-50 border-purple-200',
  ENTERPRISE: 'text-yellow-700 bg-yellow-50 border-yellow-200',
};

function formatNumber(n) {
  return n?.toLocaleString('it-IT') ?? '0';
}

function UsageBar({ used, limit, label }) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const barColor =
    pct >= 90 ? 'bg-red-500' :
    pct >= 70 ? 'bg-yellow-400' :
    'bg-blue-500';
  const textColor =
    pct >= 90 ? 'text-red-600' :
    pct >= 70 ? 'text-yellow-600' :
    'text-gray-600';

  return (
    <div className="mb-2">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-600">{label}</span>
        <span className={`font-medium ${textColor}`}>
          {formatNumber(used)} / {formatNumber(limit)} ({pct}%)
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`${barColor} h-2 rounded-full transition-all duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

const UsageWidget = ({ refreshTrigger = 0 }) => {
  const [usage, setUsage]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.getMyCompanyUsage()
      .then(data => setUsage(data))
      .catch(() => {}) // Il widget si nasconde silenziosamente in caso di errore
      .finally(() => setLoading(false));
  }, [refreshTrigger]);

  if (loading || !usage) return null;

  const planClass = PLAN_COLORS[usage.subscription_plan] || 'text-gray-600 bg-gray-50 border-gray-200';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 text-xs font-bold rounded border ${planClass}`}>
            Piano {usage.subscription_plan}
          </span>
          <span className="text-sm font-medium text-gray-700">{usage.company_name}</span>
        </div>
        <span className="text-xs text-gray-400">Utilizzo azienda</span>
      </div>

      <UsageBar
        used={usage.words_used}
        limit={usage.word_limit}
        label="Parole elaborate"
      />
      <UsageBar
        used={usage.queries_used}
        limit={usage.query_limit}
        label="Query effettuate"
      />

      {usage.period_start && (
        <div className="text-xs text-gray-400 mt-1 mb-1">
          Periodo mensile dal{' '}
          <span className="font-medium">
            {new Date(usage.period_start).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })}
          </span>
        </div>
      )}

      {(usage.word_percent >= 90 || usage.query_percent >= 90) && (
        <div className="mt-2 text-xs text-red-600 font-medium">
          Attenzione: uno o più limiti stanno per essere raggiunti.
        </div>
      )}
    </div>
  );
};

export default UsageWidget;
